type UnknownRecord = Record<string, unknown>;

export type WriterToStudioCheckout =
  | {
      checkoutUi: "hosted";
      checkoutUrl: string;
    }
  | {
      checkoutUi: "embedded";
      clientSecret: string;
      publishableKey: string;
    };

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null;

const readNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const parseCheckout = (value: unknown): WriterToStudioCheckout | null => {
  if (!isRecord(value)) {
    return null;
  }

  const checkoutUrl = readNonEmptyString(value.checkout_url);
  if (value.checkout_ui === "hosted" && checkoutUrl) {
    return {
      checkoutUi: "hosted",
      checkoutUrl,
    };
  }

  const clientSecret = readNonEmptyString(value.client_secret);
  const publishableKey = readNonEmptyString(value.stripe_publishable_key);
  if (value.checkout_ui === "embedded" && clientSecret && publishableKey) {
    return {
      checkoutUi: "embedded",
      clientSecret,
      publishableKey,
    };
  }

  return null;
};

/**
 * `useAxios` normally unwraps the HTTP response before `handleAxiosRequest` runs. A click can
 * race the interceptor effect during mount, leaving the Nest `{ data: ... }` envelope intact.
 * Accept both shapes so a valid checkout session never becomes a silent no-op.
 */
export const parseWriterToStudioCheckout = (value: unknown): WriterToStudioCheckout => {
  const direct = parseCheckout(value);
  if (direct) {
    return direct;
  }

  const enveloped = isRecord(value) ? parseCheckout(value.data) : null;
  if (enveloped) {
    return enveloped;
  }

  throw new Error("Upgrade checkout returned an invalid response.");
};
